import React, { FC, useEffect, useRef, useState } from "react";

// Hooks
import { StepName, useFormUI } from "../Hooks/form";
import { getRootData } from "../Hooks/useStepData";
import { useLabels } from "../Hooks/useLabels";
import { usePersistedSelection } from "../Hooks/usePersistedSelection";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";

// Steps
import { InitialStep } from "../Steps/InitialStep/InitialStep";
import { TreatmentStep } from "../Steps/TreatmentStep";
import { AppointmentStep } from "../Steps/AppointmentStep/AppointmentStep";
import { PersonalInfoFormStep } from "../Steps/PersonalInfoFormStep/PersonalInfoFormStep";
import { BookingConfirmedStep } from "../Steps/BookingConfirmedStep/BookingConfirmedStep";

// Types
import { RootDataType } from "../Helpers/interfaces";
import { QuickAppointmentSetupDto } from "../../BookingButton/QuickAppointmentSetupDto.csharp";

//Hepers
import { assertNever, isObjectEmpty } from "../Helpers";
import {
  findClosestParentContainerWithSpecializationName,
  findTreatmentById,
  isServiceContainer,
  isTreatmentContainer,
} from "../Helpers/treatmentsHelpers";
import { parentTreatmentInitialState } from "../Helpers/initialState/parentTreatmentInitialState";

// Elements
import { Summary } from "../Summary/Summary";
import { GoBackButton } from "../GoBackButton/GoBackButton";
import { RichTextArea } from "../RichTextArea/RichTextArea";
import { LoadingAnimation } from "../../LoadingAnimation/LoadingAnimation";
import { StorageAccessor } from "../StorageAccesor/StorageAccesor";
import { FormFields, FormReducer } from "../Helpers/formTypes";
import Modal from "react-modal";
import { GtmService } from "../GtmService";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { CloseButton } from "../CloseButton/CloseButton";

export const Booking: FC<{
  showCloseButton?: boolean;
  closeModal: () => void;
  autostartBooking: boolean;
  breadcrumbs: string[];
  ignorePersistedData?: boolean;
  quickAppointmentSetup?: QuickAppointmentSetupDto;
}> = ({
  showCloseButton,
  closeModal,
  autostartBooking,
  breadcrumbs,
  ignorePersistedData,
  quickAppointmentSetup,
}) => {
  const [rootData, setRootData] = useState<RootDataType>({
    treatments: [],
    centers: [],
    services: [],
    doctors: [],
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const {
    state,
    dispatch,
    currentStep,
    currentStepName,
    steps,
    nextStep,
    previousStep,
    setStep,
    setStepByName,
  } = useFormUI();

  const currentStepRef = useRef(currentStep);
  const { bookingLabels } = useLabels();
  const { waitlist, newBookingFilterValues } = useBookingContext();

  const modalOpenedFromWaitList =
    currentStepName == "PersonalInfoFormStep" && waitlist?.contentId;

  useEffect(() => {
    if (error != null) {
      throw new Error(error);
    }
  }, [error]);

  useEffect(() => {
    (async function () {
      try {
        if (waitlist?.contentId) {
          setStepByName("PersonalInfoFormStep");
          setLoading(false);
        } else {
          const { treatments, services, centers, doctors } =
            await getRootData();
          setRootData({
            treatments,
            services,
            centers,
            doctors,
          });

          if (
            ignorePersistedData ||
            isObjectEmpty(new StorageAccessor().getItem())
          ) {
            const { containerId, doctorId, locationId } =
              newBookingFilterValues ?? {};

            if (doctorId) {
              const foundDoctor = doctors.find((d) => d.id === doctorId);
              if (foundDoctor) {
                dispatch({
                  type: FormReducer.update,
                  field: FormFields.doctor,
                  payload: foundDoctor,
                });
				
				//Set default location/center when doctor have only single one
                const doctorCenters = centers.filter(
                  (c) => !c.isLocation && foundDoctor.centers.includes(c.id)
                );
                if (doctorCenters.length > 0) {
                  if (doctorCenters.length === 1) {
                    dispatch({
                      type: FormReducer.update,
                      field: FormFields.location,
                      payload: doctorCenters[0],
                    });
                  } else {
                    const locationIds = doctorCenters.map((c) => c.locationId);
                    const doctorLocations = centers.filter(
                      (c) => c.isLocation && locationIds.includes(c.id)
                    );
                    if (doctorLocations.length === 1) {
                      dispatch({
                        type: FormReducer.update,
                        field: FormFields.location,
                        payload: doctorLocations[0],
                      });
                    }
                  }
                }
              }
            }
			
			
            if (locationId) {
              const foundLocation = centers.find((c) => c.id === locationId);
              if (foundLocation) {
                dispatch({
                  type: FormReducer.update,
                  field: FormFields.location,
                  payload: foundLocation,
                });
              }
            }

            if (containerId) {
              const container = findTreatmentById(treatments, containerId);

              if (container) {
                const parentContainer = container.parentContainerId
                  ? findTreatmentById(treatments, container.parentContainerId)
                  : null;

                if (isServiceContainer(container)) {
                  const specializationName =
                    findClosestParentContainerWithSpecializationName(
                      container,
                      treatments
                    );
                  dispatch({
                    type: FormReducer.update,
                    field: FormFields.service,
                    payload: container,
                  });
                  dispatch({
                    type: FormReducer.update,
                    field: FormFields.treatment,
                    payload: container,
                  });
                  dispatch({
                    type: FormReducer.update,
                    field: FormFields.parentTreatment,
                    payload: parentContainer || parentTreatmentInitialState,
                  });
                  dispatch({
                    type: FormReducer.update,
                    field: FormFields.specializationName,
                    payload: specializationName,
                  });
                  dispatch({
                    type: FormReducer.update,
                    field: FormFields.breadcrumbs,
                    payload: breadcrumbs,
                  });

                  setStepByName("AppointmentStep");
                } else if (isTreatmentContainer(container)) {
                  dispatch({
                    type: FormReducer.update,
                    field: FormFields.treatment,
                    payload: container,
                  });

                  parentContainer
                    ? setStepByName("TreatmentStep")
                    : setStepByName("InitialStep");
                }
              }
            }
          }
        }
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    GtmService.bookingOverlayLoaded();
    window.history.pushState(
      null,
      `form-step-${currentStepRef.current}`,
      window.location.pathname + window.location.search
    );
    window.addEventListener("popstate", onBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    if (!waitlist) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.currentStep,
        payload: currentStep,
      });
    }
  }, [currentStep]);

  if (!ignorePersistedData) {
    usePersistedSelection(dispatch, setStep, waitlist);
  }

  useEffect(() => {
    const element = titleRef;
    if (element && element.current && !modalOpenedFromWaitList) {
      element.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentStep]);

  function onBackButtonEvent(e: PopStateEvent) {
    e.preventDefault();
    if (autostartBooking || !showCloseButton) {
      window.history.back();
      return;
    }

    closeModal();
  }

  const handleGoBackButtonClick = () => {
    const goBackToParentTreatment = () => {
      if (state.parentTreatment.parentContainerId) {
        dispatch({
          type: FormReducer.update,
          field: FormFields.treatment,
          payload: state.parentTreatment,
        });
        setStepByName("TreatmentStep");
      } else {
        setStepByName("InitialStep");
      }
    };

    switch (currentStepName) {
      case "InitialStep":
        break;
      case "PersonalInfoFormStep":
      case "BookingConfirmedStep":
        return previousStep();
      case "AppointmentStep":
        dispatch({
          type: FormReducer.clear,
          field: FormFields.service,
        });
        dispatch({
          type: FormReducer.clear,
          field: FormFields.appointment,
        });
        dispatch({
          type: FormReducer.clear,
          field: FormFields.specializationName,
        });
        dispatch({
          type: FormReducer.clear,
          field: FormFields.breadcrumbs,
        });
        goBackToParentTreatment();
        break;
      case "TreatmentStep":
        goBackToParentTreatment();
        break;
      default:
        return assertNever(currentStepName);
    }
  };

  const setStepHeading = () => {
    if (
      currentStepName === "TreatmentStep" ||
      currentStepName === "AppointmentStep"
    ) {
      if (state.parentTreatment.parentContainerId) {
        return state.parentTreatment.bookingName;
      } else {
        return steps[0]?.heading;
      }
    }
    return steps[currentStep - 1]?.heading;
  };

  function determineOptionalText(
    stepName: StepName
  ): string | null | undefined {
    switch (stepName) {
      case "InitialStep":
        const labels = bookingLabels?.stepsBookingLabels.firstStepLabels;
        return newBookingFilterValues?.doctorId
          ? labels?.optionalDescriptionDoctors
          : labels?.optionalDescription;
      default:
        return null;
    }
  }

  const shouldDisplayGoBackButton =
    currentStepName !== "InitialStep" &&
    currentStepName !== "BookingConfirmedStep";

  const shouldDisplaySummary =
    currentStepName !== "InitialStep" &&
    currentStepName !== "TreatmentStep" &&
    currentStepName !== "BookingConfirmedStep" &&
    state.service?.id;

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);
  }, []);

  return (
    <div className="Booking">
      <div className="Booking__navigation">
        {shouldDisplayGoBackButton && !loading && !waitlist && (
          <GoBackButton
            onClick={handleGoBackButtonClick}
            buttonLabel={setStepHeading()}
          />
        )}
        {showCloseButton && (
          <CloseButton onClick={closeModal} className="Booking__closeButton" />
        )}
      </div>

      <div className="Booking__content">
        {loading && (
          <LoadingAnimation
            className="loading-animation-container text-center"
            text="Laster inn"
          />
        )}
        {!loading && (
          <ProgressBar
            currentStep={currentStep}
            currentStepName={currentStepName}
            totalSteps={steps.length}
            treatments={rootData.treatments}
            currentTreatment={state.treatment}
          />
        )}
        {!loading &&
          (() => {
            switch (currentStepName) {
              case "InitialStep":
                return (
                  <InitialStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                    quickAppointmentSetup={quickAppointmentSetup}
                    setStepByName={setStepByName}
                  />
                );
              case "TreatmentStep":
                return (
                  <TreatmentStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                  />
                );
              case "AppointmentStep":
                return (
                  <AppointmentStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                  />
                );
              case "PersonalInfoFormStep":
                return (
                  <PersonalInfoFormStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                  />
                );
              case "BookingConfirmedStep":
                return (
                  <BookingConfirmedStep state={state} rootData={rootData} />
                );
              default:
                return null;
            }
          })()}
        {shouldDisplaySummary && !loading && (
          <Summary state={state} step={currentStep} rootData={rootData} />
        )}
        {!loading && (
          <RichTextArea
            className="Booking__optionalText"
            content={determineOptionalText(currentStepName)}
          />
        )}
      </div>
    </div>
  );
};
